import flatMap from 'lodash/flatMap'

interface IFormattedTuitionProgram {
  rates: IFormattedTuitionRate[]
  title: string
}

interface IFormattedTuitionRate {
  days: string
  amount: string
  weeklyAmount: string
}

interface IParsedTuitionRate {
  days: string
  amount: number | null
  weeklyAmount: number | null
}

export interface IPricingRange {
  min: number
  max: number
  weeklyMin: number | null
  weeklyMax: number | null
}

export function profileEnrollmentPricing(profileEnrollment: string): IPricingRange | null {
  const enrollment = JSON.parse(profileEnrollment)
  const programs: IFormattedTuitionProgram[] = enrollment.schedules || []

  const rates: IParsedTuitionRate[] = flatMap(programs, toParsedTuitionRates)
  const monthlyAmounts = rates.map(r => r.amount).filter(notEmpty)
  const weeklyAmounts = rates.map(r => r.weeklyAmount).filter(notEmpty)

  const { min, max } = calculateRange(monthlyAmounts)
  const { min: weeklyMin, max: weeklyMax } = calculateRange(weeklyAmounts)

  if (min !== null && max !== null) {
    return { min, max, weeklyMin, weeklyMax }
  }

  return null
}

function toParsedTuitionRates(program: IFormattedTuitionProgram): IParsedTuitionRate[] {
  return (program.rates || []).map(r => ({
    days: r.days,
    amount: parseAmount(r.amount || ''),
    weeklyAmount: parseAmount(r.weeklyAmount || ''),
  }))
}

// Parse amount string as number in cents (e.g. '$42' -> 4200)
function parseAmount(amount: string) {
  const value = parseInt(amount.replace(/\$|,/g, ''), 10)
  if (Number.isNaN(value)) return null

  return value * 100
}

function calculateRange(amounts: number[]) {
  if (amounts.length === 0) return { max: null, min: null }
  return { max: Math.max(...amounts), min: Math.min(...amounts) }
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}
